/* eslint-disable @typescript-eslint/no-unused-vars */
import dynamic from 'next/dynamic'
const Cell = dynamic(() => import('./ComparisonChartCell'))
const CellHtml = dynamic(() => import('./ComparisonChartCellHtml'))
const Row = dynamic(() => import('./ComparisonChartRow'))
import times from 'lodash/times'
import PropTypes from 'prop-types'
import LazyLoad from 'react-lazy-load'
import React, { Fragment, useCallback, useRef, useState } from 'react'
import ImageFactory from '../../adapters/cloudinary/ImageFactory'
import Block from '../../adapters/helpers/Block'
import { ComparisonChartBlockConstants, ProductHighlightsConstants } from '../../adapters/helpers/Constants'
import { smoothVerticalScroll, stripHtml } from '../../adapters/helpers/Utils'
import { Label } from '../Label/Label'
import { Markdown } from '../MarkdownText/MarkdownText'
import { mediaQueryHOC } from '../../adapters/helpers/Hooks'
import { optimizeImage } from '../../adapters/helpers/pagespeedHelper'
const Button = dynamic(() => import('../Button/Button'))
const ObLink = dynamic(() => import('../ObLink/ObLink'))
const Icon = dynamic(() => import('../Icon/Icon'))

const MINIMUM_WIDTH_MOBILE_VW = 40
const PADDING_HORIZONTAL_MOBILE_PX = 39

function ComparisonChart(props) {
    const { extraAttributes, className, onClickCallback, isMedium } = props
    const entity = extraAttributes?.entity
    const block = new Block(props)
    const leftFadeRef = useRef(null)
    const rightFadeRef = useRef(null)
    const noValueLabel = Label({ label: block.getFieldValue(ComparisonChartBlockConstants.noValueLabel) })
    const jumpToConfiguratorLink = block.getFieldValue(ComparisonChartBlockConstants.jumpToConfiguratorLink)
    const [learnMore, setLearnMore] = useState(true)
    const learnMoreCta = block.getFieldValue('learnMoreCta')
    const isExpandable = block.getFieldValue('isExpandable')

    const onChartScroll = useCallback((event) => {
        let scrollRatio = event.target.scrollLeft / (event.target.scrollWidth - event.target.offsetWidth)
        scrollRatio = Math.max(0, scrollRatio)
        scrollRatio = Math.min(1, scrollRatio)

        if (leftFadeRef.current) {
            leftFadeRef.current.style.opacity = scrollRatio
        }

        if (rightFadeRef.current) {
            rightFadeRef.current.style.opacity = 1 - scrollRatio
        }
    }, [])
    const series = entity?.series

    const productSeriesValues = []
    series?.forEach((serie) => {
        serie?.fields?.products?.forEach((product) => {
            product?.fields?.productVariants?.forEach((variant) => {
                if (variant?.fields?.comparisonPoints?.length > 0) {
                    productSeriesValues.push({
                        ...variant,
                        slug: product?.fields?.slug,
                        buyNowLabel: product?.fields?.buyNowLabel?.fields?.text,
                        isEnableCommingSoon: product?.fields?.enableComingSoonLabel,
                        deactivateBuyNowButton: product?.fields?.deactivateBuyNowButton,
                    })
                }
            })
        })
    })
    const productSeries = productSeriesValues.filter((product) => product.fields.shortTitle)
    let [seriesCopy, setSriesCopy] = useState(productSeries.slice(0, 3))
    const title = entity?.comparisonChart?.fields?.title
    const classNames = ['ob-comparison-chart']

    const jumpToConfigurator = () => {
        const configuratorElem = document.querySelector(jumpToConfiguratorLink?.fields?.url)
        smoothVerticalScroll(configuratorElem, ComparisonChartBlockConstants.top)
    }

    if (className) {
        classNames.push(className)
    }

    const priceRangeLabels = seriesCopy?.map((serie) => {
        return {
            label: serie?.fields?.priceRangeLabel?.fields?.text,
            ariaLabel: serie?.fields?.priceRangeAriaLabel,
        }
    })
    const comparisonPoints = seriesCopy?.map((serie) => {
        const comparisonPointFallback = serie?.fields?.comparisonPointsFallback
        return (
            serie?.fields?.comparisonPoints?.map((comparisonPoint) => {
                return {
                    cellTitle: comparisonPoint?.fields?.shortName,
                    label: comparisonPoint?.fields?.text,
                    className: comparisonPoint?.fields?.classNames,
                    comparisonPointFallback: comparisonPointFallback,
                }
            }) || []
        )
    })

    const setSelectedProduct = (index, anchorId) => {
        const product = productSeries.find((product) => product?.fields?.sku === anchorId)
        const updatedSeries = seriesCopy.map((currentSeries, selectedProduct) => {
            if (index == selectedProduct) {
                return product
            } else {
                return currentSeries
            }
        })
        setSriesCopy(updatedSeries)
    }

    const renderBuyNowCTA = (buyNowLabel, isEnableCommingSoon, buyNowDeactivated, buyNowDataActionDetail, sku, dataBorderTheme) => {
        const btnClassName = isEnableCommingSoon ? 'find-a-retailer-secondary-btn-style' : ''
        return (
            buyNowLabel &&
            !buyNowDeactivated && (
                <Button
                    tag={isEnableCommingSoon ? 'a' : 'button'}
                    textTheme={isEnableCommingSoon}
                    onClick={(event) => onClickCallback(event)}
                    className={`${btnClassName} event_buy_now`}
                    borderTheme={isMedium}
                    dataActionDetail={sku}
                    size='medium'
                    sku={sku}
                >
                    {buyNowLabel}
                </Button>
            )
        )
    }

    const RenderComparisonSelect = ({ productSeries, currentSeries, setSelectedProduct, shortTitle, altText, imageSrc, i }) => {
        return (
            <Fragment>
                <select
                    required={true}
                    aria-label={ComparisonChartBlockConstants.ariaLabel}
                    name={'spotlight_experience_country'}
                    className={'ob-comparison-chart-short-title-select'}
                    data-reset-country-code={true}
                    onChange={(event) => {
                        setSelectedProduct(i, event.target.value)
                        if (window && window.dataLayer) {
                            window.dataLayer.push({
                                event: 'customEvent',
                                GAeventCategory: 'event_informational_action',
                                GAeventAction: 'event_product_compare',
                                GAeventLabel: event.target.value,
                            })
                        }
                    }}
                >
                    {Array.from(new Set(productSeries.map((s) => s.fields?.sku))).map((sku, index) => {
                        const product = productSeries.find((s) => s.fields?.sku === sku)
                        const productLabel = product.fields?.sku
                        const productValue = product.fields?.shortTitle
                        const selected = product?.fields?.sku === currentSeries?.fields?.sku
                        return <option key={'selectOption-' + index} value={productLabel} selected={selected} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(productValue) }}></option>
                    })}
                </select>
                <a href={currentSeries?.slug?.fields?.slug} className='event_internal_link' title={stripHtml(shortTitle)} aria-label={stripHtml(shortTitle)} data-action-detail={stripHtml(shortTitle)}>
                    <div className='ob-comparison-chart-image'>
                        <LazyLoad offset={100}>
                            <img className='ob-comparison-chart-image-height' alt={`${ComparisonChartBlockConstants.altTextPrefixFeatureImage}-${altText}`} src={optimizeImage(imageSrc)} fetchPriority="high" />
                        </LazyLoad>
                    </div>
                </a>
                <div className='find-retailer-cta'>
                    {renderBuyNowCTA(currentSeries?.buyNowLabel, currentSeries?.isEnableCommingSoon, currentSeries?.deactivateBuyNowButton, currentSeries?.buyNowDataActionDetail, currentSeries?.fields?.sku)}
                </div>
            </Fragment>
        )
    }

    function comparisonMobileView() {
        const longestComparision = comparisonPoints?.reduce((p, c) => (c.length > p ? c.length : p), 0)
        const seriesLabel = comparisonPoints.filter((compaire) => compaire.length == longestComparision)
        if (learnMore && isExpandable) {
            return (
                <LazyLoad offset={100}>
                    <div className='ob-comparison-chart-wrapper-1'>
                        {Array.isArray(seriesCopy) && (
                            <div onScroll={onChartScroll} className='ob-comparison-chart-wrapper-2'>
                                {seriesCopy.map((_, i) => (
                                    <div key={i} className='ob-comparison-chart-snap-hack' style={{ left: `${i * MINIMUM_WIDTH_MOBILE_VW}vw` }} />
                                ))}
                                <div className='ob-comparison-chart-table ob-comparison-chart-table-width' role={ComparisonChartBlockConstants.table}>
                                    <Row>
                                        {seriesCopy.slice(0, 2).map((currentSeries, i) => {
                                            const asset = currentSeries?.fields?.mainAsset
                                            const altText = asset?.fields?.alternateText
                                            const shortTitle = altText
                                            const contentImageId = currentSeries?.fields?.productRecommendedMainAsset?.fields?.asset?.fields?.file?.url
                                            const imageSrc = contentImageId && ImageFactory.buildContentfullImageUrlByHeight(contentImageId, '220')

                                            return (
                                                <CellHtml key={i}>
                                                    <RenderComparisonSelect
                                                        productSeries={productSeries}
                                                        currentSeries={currentSeries}
                                                        setSelectedProduct={setSelectedProduct}
                                                        shortTitle={shortTitle}
                                                        altText={altText}
                                                        imageSrc={imageSrc}
                                                        i={i}
                                                    />
                                                </CellHtml>
                                            )
                                        })}
                                    </Row>
                                    <Row>
                                        {priceRangeLabels.map((priceRangeLabel, i) => (
                                            <Cell key={i} className='ob-comparison-chart-price-range' ariaLabel={priceRangeLabel.ariaLabel} role={ComparisonChartBlockConstants.cell}>
                                                {priceRangeLabel.label}
                                            </Cell>
                                        ))}
                                    </Row>
                                    {times(1, (i) => (
                                        <Row key={`feature-row__${i}`}>
                                            {comparisonPoints.slice(0, 2).map((comparisonPoint, j) => (
                                                <Cell key={j} className='ob-comparison-chart-feature-cell' role={ComparisonChartBlockConstants.cell}>
                                                    {comparisonPoint[i] ? (
                                                        // ClassName visuallyhidden can be passed through the data for A11y text
                                                        // else it'll use the fallback
                                                        <>
                                                            {j == 0 ? (
                                                                <div className='ob-comparison-chart-cell-title-card'>
                                                                    <Markdown className={seriesLabel[0][i].className} source={seriesLabel[0][i].cellTitle} />
                                                                </div>
                                                            ) : (
                                                                <div className='ob-comparison-chart-cell-hidden'>
                                                                    <Markdown className={seriesLabel[0][i].className} source={seriesLabel[0][i].cellTitle} />
                                                                </div>
                                                            )}
                                                            <Markdown className={comparisonPoint[i].className} source={comparisonPoint[i].label} />
                                                        </>
                                                    ) : (
                                                        // FallBack if empty field
                                                        <p className='visuallyhidden'>{comparisonPoint[0]?.comparisonPointFallback || noValueLabel}</p>
                                                    )}
                                                </Cell>
                                            ))}
                                        </Row>
                                    ))}
                                </div>
                            </div>
                        )}
                        {learnMoreButton()}
                        <div className='ob-comparison-chart-fade-left' ref={leftFadeRef} />
                        <div className='ob-comparison-chart-fade-right' ref={rightFadeRef} />
                    </div>
                </LazyLoad>
            )
        } else {
            return (
                <LazyLoad offset={100}>
                    <div className='ob-comparison-chart-wrapper-1'>
                        {Array.isArray(seriesCopy) && (
                            <div onScroll={onChartScroll} className='ob-comparison-chart-wrapper-2'>
                                {seriesCopy.map((_, i) => (
                                    <div key={i} className='ob-comparison-chart-snap-hack' style={{ left: `${i * MINIMUM_WIDTH_MOBILE_VW}vw` }} />
                                ))}
                                <div className='ob-comparison-chart-table ob-comparison-chart-table-width' role={ComparisonChartBlockConstants.table}>
                                    <Row>
                                        {seriesCopy.slice(0, 2).map((currentSeries, i) => {
                                            const asset = currentSeries?.fields?.mainAsset
                                            const altText = asset?.fields?.alternateText
                                            const shortTitle = altText
                                            const contentImageId = currentSeries?.fields?.productRecommendedMainAsset?.fields?.asset?.fields?.file?.url
                                            const imageSrc = contentImageId && ImageFactory.buildContentfullImageUrlByHeight(contentImageId, '220')

                                            return (
                                                <CellHtml key={i}>
                                                    <RenderComparisonSelect
                                                        productSeries={productSeries}
                                                        currentSeries={currentSeries}
                                                        setSelectedProduct={setSelectedProduct}
                                                        shortTitle={shortTitle}
                                                        altText={altText}
                                                        imageSrc={imageSrc}
                                                        i={i}
                                                    />
                                                </CellHtml>
                                            )
                                        })}
                                    </Row>
                                    <Row>
                                        {priceRangeLabels.map((priceRangeLabel, i) => (
                                            <Cell key={i} className='ob-comparison-chart-price-range' ariaLabel={priceRangeLabel.ariaLabel} role={ComparisonChartBlockConstants.cell}>
                                                {priceRangeLabel.label}
                                            </Cell>
                                        ))}
                                    </Row>
                                    {times(longestComparision, (i) => (
                                        <Row key={`feature-row__${i}`}>
                                            {comparisonPoints.slice(0, 2).map((comparisonPoint, j) => (
                                                <Cell key={j} className='ob-comparison-chart-feature-cell' role={ComparisonChartBlockConstants.cell}>
                                                    {comparisonPoint[i] ? (
                                                        // ClassName visuallyhidden can be passed through the data for A11y text
                                                        // else it'll use the fallback
                                                        <>
                                                            {j == 0 ? (
                                                                <div className='ob-comparison-chart-cell-title-card'>
                                                                    <Markdown className={seriesLabel[0][i].className} source={seriesLabel[0][i].cellTitle} />
                                                                </div>
                                                            ) : (
                                                                <div className='ob-comparison-chart-cell-hidden'>
                                                                    <Markdown className={seriesLabel[0][i].className} source={seriesLabel[0][i].cellTitle} />
                                                                </div>
                                                            )}
                                                            <Markdown className={comparisonPoint[i].className} source={comparisonPoint[i].label} />
                                                        </>
                                                    ) : (
                                                        // FallBack if empty field
                                                        <p className='visuallyhidden'>{comparisonPoint[0]?.comparisonPointFallback || noValueLabel}</p>
                                                    )}
                                                </Cell>
                                            ))}
                                        </Row>
                                    ))}
                                </div>
                            </div>
                        )}
                        {learnMoreButton()}
                        <div className='ob-comparison-chart-fade-left' ref={leftFadeRef} />
                        <div className='ob-comparison-chart-fade-right' ref={rightFadeRef} />
                    </div>
                </LazyLoad>
            )
        }
    }

    function comparisonDesktopView() {
        const longestComparision = comparisonPoints?.reduce((p, c) => (c.length > p ? c.length : p), 0)
        const seriesLabel = comparisonPoints.filter((compaire) => compaire.length == longestComparision)
        if (learnMore && isExpandable) {
            return (
                <div className='ob-comparison-chart-wrapper-1 expanded'>
                    {Array.isArray(seriesCopy) && (
                        <div onScroll={onChartScroll} className='ob-comparison-chart-wrapper-2'>
                            {seriesCopy.map((_, i) => (
                                <div key={i} className='ob-comparison-chart-snap-hack' style={{ left: `${i * MINIMUM_WIDTH_MOBILE_VW}vw` }} />
                            ))}
                            <div className='ob-comparison-chart-table' role={ComparisonChartBlockConstants.table} style={{ width: `calc(${MINIMUM_WIDTH_MOBILE_VW * seriesCopy.length}vw + ${2 * PADDING_HORIZONTAL_MOBILE_PX}px)` }}>
                                <Row>
                                    <Cell className='ob-comparison-chart-header' />
                                    {seriesCopy.map((currentSeries, i) => {
                                        const shortTitle = currentSeries?.fields?.shortTitle

                                        const asset = currentSeries?.fields?.mainAsset
                                        const altText = asset?.fields?.alternateText
                                        const contentImageId = currentSeries?.fields?.productRecommendedMainAsset?.fields?.asset?.fields?.file?.url
                                        const imageSrc = contentImageId && ImageFactory.buildContentfullImageUrlByHeight(contentImageId, '220')
                                        return (
                                            <CellHtml key={i} className='ob-comparison-chart-header' role={ComparisonChartBlockConstants.columnheader}>
                                                <RenderComparisonSelect
                                                    productSeries={productSeries}
                                                    currentSeries={currentSeries}
                                                    setSelectedProduct={setSelectedProduct}
                                                    shortTitle={shortTitle}
                                                    altText={altText}
                                                    imageSrc={imageSrc}
                                                    i={i}
                                                />
                                            </CellHtml>
                                        )
                                    })}
                                </Row>
                                <Row>
                                    {priceRangeLabels.map((priceRangeLabel, i) => (
                                        <Cell key={i} className='ob-comparison-chart-price-range' ariaLabel={priceRangeLabel.ariaLabel} role={ComparisonChartBlockConstants.cell}>
                                            {priceRangeLabel.label}
                                        </Cell>
                                    ))}
                                </Row>
                                {times(1, (i) => (
                                    <Row key={`feature-row__${i}`}>
                                        <Cell key={i} className='ob-comparison-chart-feature-cell' role={ComparisonChartBlockConstants.cell}>
                                            <Markdown className={seriesLabel[0][i].className} source={seriesLabel[0][i].cellTitle} />
                                        </Cell>
                                        {comparisonPoints.map((comparisonPoint, j) => (
                                            <Cell key={j} className='ob-comparison-chart-feature-cell' role={ComparisonChartBlockConstants.cell}>
                                                {comparisonPoint[i] ? (
                                                    // ClassName visuallyhidden can be passed through the data for A11y text
                                                    // else it'll use the fallback
                                                    <Markdown className={comparisonPoint[i].className} source={comparisonPoint[i].label} />
                                                ) : (
                                                    // FallBack if empty field
                                                    <p className='visuallyhidden'>{comparisonPoint[0]?.comparisonPointFallback || noValueLabel}</p>
                                                )}
                                            </Cell>
                                        ))}
                                    </Row>
                                ))}
                            </div>
                        </div>
                    )}
                    {learnMoreButton()}
                    <div className='ob-comparison-chart-fade-left' ref={leftFadeRef} />
                    <div className='ob-comparison-chart-fade-right' ref={rightFadeRef} />
                </div>
            )
        } else {
            return (
                <div className='ob-comparison-chart-wrapper-1'>
                    {Array.isArray(seriesCopy) && (
                        <div onScroll={onChartScroll} className='ob-comparison-chart-wrapper-2'>
                            {seriesCopy.map((_, i) => (
                                <div key={i} className='ob-comparison-chart-snap-hack' style={{ left: `${i * MINIMUM_WIDTH_MOBILE_VW}vw` }} />
                            ))}
                            <div className='ob-comparison-chart-table' role={ComparisonChartBlockConstants.table} style={{ width: `calc(${MINIMUM_WIDTH_MOBILE_VW * seriesCopy.length}vw + ${2 * PADDING_HORIZONTAL_MOBILE_PX}px)` }}>
                                <Row>
                                    <Cell className='ob-comparison-chart-header' />
                                    {seriesCopy.map((currentSeries, i) => {
                                        const shortTitle = currentSeries?.fields?.shortTitle

                                        const asset = currentSeries?.fields?.mainAsset
                                        const altText = asset?.fields?.alternateText
                                        const contentImageId = currentSeries?.fields?.productRecommendedMainAsset?.fields?.asset?.fields?.file?.url
                                        const imageSrc = contentImageId && ImageFactory.buildContentfullImageUrlByHeight(contentImageId, '220')
                                        return (
                                            <CellHtml key={i} className='ob-comparison-chart-header' role={ComparisonChartBlockConstants.columnheader}>
                                                <RenderComparisonSelect
                                                    productSeries={productSeries}
                                                    currentSeries={currentSeries}
                                                    setSelectedProduct={setSelectedProduct}
                                                    shortTitle={shortTitle}
                                                    altText={altText}
                                                    imageSrc={imageSrc}
                                                    i={i}
                                                />
                                            </CellHtml>
                                        )
                                    })}
                                </Row>
                                <Row>
                                    {priceRangeLabels.map((priceRangeLabel, i) => (
                                        <Cell key={i} className='ob-comparison-chart-price-range' ariaLabel={priceRangeLabel.ariaLabel} role={ComparisonChartBlockConstants.cell}>
                                            {priceRangeLabel.label}
                                        </Cell>
                                    ))}
                                </Row>
                                {times(longestComparision, (i) => (
                                    <Row key={`feature-row__${i}`}>
                                        <Cell key={i} className='ob-comparison-chart-feature-cell' role={ComparisonChartBlockConstants.cell}>
                                            <Markdown className={seriesLabel[0][i].className} source={seriesLabel[0][i].cellTitle} />
                                        </Cell>
                                        {comparisonPoints.map((comparisonPoint, j) => (
                                            <Cell key={j} className='ob-comparison-chart-feature-cell' role={ComparisonChartBlockConstants.cell}>
                                                {comparisonPoint[i] ? (
                                                    // ClassName visuallyhidden can be passed through the data for A11y text
                                                    // else it'll use the fallback
                                                    <Markdown className={comparisonPoint[i].className} source={comparisonPoint[i].label} />
                                                ) : (
                                                    // FallBack if empty field
                                                    <p className='visuallyhidden'>{comparisonPoint[0]?.comparisonPointFallback || noValueLabel}</p>
                                                )}
                                            </Cell>
                                        ))}
                                    </Row>
                                ))}
                            </div>
                        </div>
                    )}
                    {learnMoreButton()}
                    <div className='ob-comparison-chart-fade-left' ref={leftFadeRef} />
                    <div className='ob-comparison-chart-fade-right' ref={rightFadeRef} />
                </div>
            )
        }
    }
    const learnMoreButton = () => {
        let button
        if (learnMoreCta) {
            if (learnMore) {
                button = (
                    <ObLink
                        ariaExpanded={!learnMore ? true : false}
                        className='blue ob-view-more event_button_click'
                        tag={ProductHighlightsConstants.button}
                        ariaLabel={ProductHighlightsConstants.learnMore}
                        onClick={handleLearnMore}
                        dataActionDetail={learnMoreCta}
                    >
                        <Icon name='chevronDown' />
                        {learnMoreCta}
                        <span className='visuallyhidden'>{learnMoreCta}</span>
                    </ObLink>
                )
            }
        }

        return button
    }
    const handleLearnMore = () => {
        setLearnMore(!learnMore)
    }
    return (
        <div className={classNames.join(' ')}>
            <h2 className='ob-comparison-chart-title'>{title}</h2>
            <p className='ob-comparison-chart-jump-to-configurator'>{jumpToConfiguratorLink?.fields?.title}</p>
            {isMedium ? comparisonMobileView() : comparisonDesktopView()}
        </div>
    )
}

export default mediaQueryHOC(ComparisonChart)
ComparisonChart.propTypes = {
    extraAttributes: PropTypes.any,
    className: PropTypes.any,
    props: PropTypes.object,
    onClickCallback: PropTypes.func,
    isMedium: PropTypes.bool,
}
